/**
 * A-09 接続完了ステップ（D-78・FR-125／**氏名必須＝D-87**）＝**招待コードを入れた直後の着地**。
 *
 * ★**なぜ要るか**（stg 一巡 F4・U12）：以前は redeem が成功しても**どこへも遷移しなかった**。
 *   とくに初回（接続0本→1本）は、接続が増えた瞬間にアプリの分岐が切り替わって
 *   コンポーネントごと差し替わるため、「つながりました」の表示は**消え**、URL に残った
 *   `?invite=` が入力欄に**使用済みのコードを再充填**して、招待画面に着地して見えた。
 *   ＝「押したのに何も起きていない」ようにしか見えない。
 *
 * ★ここでやること＝①つながった卸を伝える ②お名前（**必須**）・電話・いつものお届け先を
 *   その場で登録（A-07 は下部タブにあるが、自分で開かない限り永久に未登録のままだった＝U12）
 *   ③注文へ送り出す。
 *
 * ★**M1.6（D-87）＝お名前だけ必須にした**（電話・住所は任意のまま）。統合カート・現場アドレス帳の
 *   共有・チーム利用で「誰の注文か」が業務の前提になり、卸側の発注者特定（T-D25＝LINE の表示名は
 *   本名とは限らない）を入口で解決する。**「あとで登録する」は電話・住所だけのスキップ**に変わった
 *   （氏名を空のまま離脱するボタンは置かない）。
 * ★**ボタンは disabled にしない**＝押せない理由が画面に出ないまま止まる。押したらその場で
 *   「お名前をご入力ください」と言う（読み上げにも乗る）。
 * ★迂回した場合（ブラウザバック等）は**チェックアウトのゲート**（A-06）とサーバの 422 が受け止める。
 *
 * ★**M1.8（D-96⑤）＝どの卸の話をしているかは `location.state.tenantId` で受け取る**。
 *   以前は App が持っていた「絞り込みの値」がその役をしていたが、M1.8 で絞り込みごと撤去した。
 *   ここを「接続の先頭」で代用すると、**2社目の招待コードを入れた職人に1社目の卸名を見せ、
 *   入力された氏名を1社目のプロフィールへ保存する**（＝新しい卸の氏名は空のままで、次の注文が
 *   チェックアウトの氏名ゲートで止まる）。**渡し元は SwitchTenant の redeem 成功時の nav 1箇所**。
 */

import type { BuilderProfile, ConnectedTenant, partnerApi } from "@kenzai/api-client";
import { Card, ErrorNote, Skeleton, Spinner } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Input } from "./formKit";

export function Welcome({
  client,
  connections,
}: {
  client: ReturnType<typeof partnerApi>;
  connections: ConnectedTenant[];
}) {
  const wanted = (useLocation().state as { tenantId?: number } | null)?.tenantId ?? null;
  // 指定があればその卸。**指定が無い直接アクセス**（履歴・リロード）は先頭の接続に落とす。
  const tenant = wanted === null ? connections[0] : connections.find((c) => c.tenant_id === wanted);

  // ★指定された卸がまだ一覧に無い＝redeem 直後の**接続一覧を取り直している最中**。
  //   ここで先頭の卸に落とすと、上のコメントの事故がそのまま起きる（1社目の名前が一瞬出て、
  //   その状態で保存されうる）。**名前を出さずに待つ**のが正しい（すぐ差し替わる）。
  if (!tenant) return <Spinner />;
  return <WelcomeForm client={client} tenant={tenant} />;
}

function WelcomeForm({
  client,
  tenant,
}: {
  client: ReturnType<typeof partnerApi>;
  tenant: ConnectedTenant;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["profile", tenant.tenant_id],
    queryFn: () => client.profile(tenant.tenant_id),
  });

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    const p = q.data;
    if (!p) return;
    setName(p.person_name ?? "");
    setPhone(p.phone ?? "");
    setAddress(p.address ?? "");
  }, [q.data]);

  const save = useMutation({
    mutationFn: ({ current, withContact }: { current: BuilderProfile; withContact: boolean }) =>
      client.updateProfile(tenant.tenant_id, {
        person_name: name.trim(),
        // 「あとで登録する」＝電話・住所だけを飛ばす（氏名は必ず入れて進む）
        phone: withContact && phone.trim() !== "" ? phone.trim() : null,
        address: withContact && address.trim() !== "" ? address.trim() : null,
        updated_at: current.updated_at,
      }),
    onSuccess: (p) => {
      qc.setQueryData(["profile", tenant.tenant_id], p);
      // 注文へ送り出す＝統合カタログ（全卸ぶんが並ぶ）。M1.8 で解除する絞り込み自体が無くなった。
      nav("/catalog");
    },
  });

  function submit(withContact: boolean) {
    if (name.trim() === "") {
      setNameError(true);
      return;
    }
    setNameError(false);
    if (q.data) save.mutate({ current: q.data, withContact });
  }

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--kz-font-size-xl)", margin: 0 }} data-testid="welcome-title">
          {tenant.tenant_name} とつながりました
        </h1>
        <p style={{ color: "var(--kz-color-ink-muted)", marginTop: "var(--kz-space-2)" }}>
          これで、この卸に注文できるようになりました。
        </p>
      </div>

      <Card>
        <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          お名前をご登録ください
        </h2>
        <p
          style={{
            margin: "0 0 var(--kz-space-4)",
            color: "var(--kz-color-ink-muted)",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          {/* なぜ聞くのかを書く（＝卸が誰からの注文か分かる／連絡が取れる） */}
          注文したときに、{tenant.tenant_name} に「誰からの注文か」が伝わります。あとから
          「プロフィール」でも変更できます。
        </p>
        <ErrorNote error={save.error} />
        {/* 登録済みの内容が取れなかったときに黙って空欄を出さない（保存も通らない＝理由を出す） */}
        {q.isError && <ErrorNote error={q.error} />}
        {/* ★**登録済みの内容が届くまで入力欄そのものを出さない**。
            この画面は開いた直後に `GET /partner/tenants/{id}/profile` を投げ、返ってきた値を
            フォームに流し込む（掛け持ちの方は前に入れた氏名が初期値として入る）。入力欄を先に
            出すと、**打ち込んでいる最中に応答が届いて打った文字が消える**（実測で踏んだ）。
            出す・出さないで受ければ、打てるようになった時点で必ず流し込みが済んでいる。 */}
        {q.data === undefined ? (
          <Skeleton rows={3} height={44} />
        ) : (
          <>
            <Input
              label="お名前（必須）"
              value={name}
              onChange={(v) => {
                setName(v);
                if (v.trim() !== "") setNameError(false);
              }}
              maxLength={100}
              required
              data-testid="welcome-name"
            />
            {nameError && (
              <p
                role="alert"
                data-testid="welcome-name-error"
                style={{ margin: "0 0 var(--kz-space-3)", color: "var(--kz-status-danger)", fontSize: "var(--kz-font-size-sm)" }}
              >
                お名前をご入力ください。ご注文のときに必要です。
              </p>
            )}
            <Input label="電話番号（任意）" value={phone} onChange={setPhone} type="tel" data-testid="welcome-phone" />
            <Input
              label="いつものお届け先（任意）"
              value={address}
              onChange={setAddress}
              hint="ご注文のときに現場が一覧に無ければ、ここの住所が初期値として入ります。"
              data-testid="welcome-address"
            />
          </>
        )}
      </Card>

      <button
        type="button"
        data-testid="welcome-save"
        disabled={save.isPending || !q.data}
        onClick={() => submit(true)}
        className="kz-pressable"
        style={{
          minHeight: 52,
          border: "none",
          borderRadius: "var(--kz-radius-sm)",
          background: "var(--kz-color-primary)",
          color: "var(--kz-color-ink-inverse)",
          font: "inherit",
          fontSize: "var(--kz-font-size-md)",
          fontWeight: 700,
          cursor: save.isPending ? "not-allowed" : "pointer",
        }}
      >
        保存して注文をはじめる
      </button>
      <button
        type="button"
        data-testid="welcome-skip"
        disabled={save.isPending || !q.data}
        onClick={() => submit(false)}
        className="kz-pressable"
        style={{
          minHeight: 44,
          border: "1px solid var(--kz-color-border)",
          borderRadius: "var(--kz-radius-sm)",
          background: "var(--kz-color-surface)",
          font: "inherit",
          cursor: "pointer",
        }}
      >
        電話・お届け先はあとで登録する
      </button>
    </div>
  );
}
